import type { DateTime } from "luxon";
import { useMemo } from "react";
import { useHospitalData } from "../contexts/HospitalDataProvider";
import { useNurseOvertimeMap } from "./useNurseOvertimeMap";

/**
 * Determines whether a nurse has weekly overtime during the week containing `referenceDate`.
 * The hook scopes the OT query to that single week — callers that render multiple weeks
 * (e.g. master schedule, monthly view) instantiate one hook call per cell, and TanStack
 * Query's cache dedupes identical (nurse, week) queries into a single network request.
 *
 * `referenceDate` must be a **logical (staffing) date** — i.e. the calendar day the BE
 * would attribute work to. Grid-day dates are already logical; convert a shift's
 * wall-clock `startTime` with `getShiftDisplayDate(startTime, dept.dayStart)` first.
 * Undefined skips the query.
 */
export function useNurseWeeklyOT({
	nurseId,
	referenceDate,
	enabled = true,
}: {
	nurseId: number;
	referenceDate: DateTime | undefined;
	/** Pass false to skip OT check entirely (e.g., synergy mode) */
	enabled?: boolean;
}): boolean {
	const { chosenDepartment, timezone, getWeekRange } = useHospitalData();
	// Empty dates disable the underlying query (see useNurseOvertimeMap `enabled` gate) — this
	// also suppresses the network call in synergy/medical depts where `enabled` is false.
	const { startDate, endDate } = enabled && referenceDate ? getWeekRange(referenceDate) : { startDate: "", endDate: "" };
	const { data: nurseOvertimeMap } = useNurseOvertimeMap(chosenDepartment.department_id, startDate, endDate, timezone, false);

	return useMemo(() => {
		if (!enabled || !nurseOvertimeMap?.[nurseId]) return false;
		return Object.values(nurseOvertimeMap[nurseId]).some((info) => info.type === "weekly" && info.hours > 0);
	}, [enabled, nurseOvertimeMap, nurseId]);
}
